import { useState, useEffect, useRef, useMemo, forwardRef, useImperativeHandle} from "react";
import { useDispatch, useSelector } from 'react-redux';
import { Container,TextInput,Select,Text, NumberInput,Alert, Group,Loader,Tooltip,Anchor,Image,Modal} from '@mantine/core';
import { useForm } from "@mantine/form";
import { useDebouncedValue } from "@mantine/hooks";
import { Row,Col} from 'react-bootstrap';
import { IconInfoCircle, IconMapPin } from "@tabler/icons-react";
import { SaveResponses } from "../actions/ResponsesActions";
import TypeTitleEnum  from '../enums/TypeTitleEnum';
import StudyLevelEnum from '../enums/StudyLevelEnum';
import { GetPaises,GetProvincias,GetCue} from "../actions/FormDataActions"
import { stepIds} from "../enums/fieldsDataConfig";
import { useStepResponses } from '../hooks/useStepResponses';
import useFormTracking from "../hooks/useFormTracking";
import debounce from "../hooks/Debounce";
import imagenCue from "../assets/cue.jpg";

const currentYear = new Date().getFullYear();
const years = Array.from({ length: 100 }, (_, index) => currentYear - index);

// Elegir una de estas no es completar la sección: la deshabilita y la exime de validar.
const SIN_ESTUDIOS_NO_CULMINADO = ['noespecificado', 'noestudio'];

const CAMPOS_SUPERIOR = [
  'tipoTituloSuperior', 'tituloSuperior', 'institucionSuperior', 'localidadSuperior', 'anioEgresoSuperior',
];
const CAMPOS_NO_CULMINADO = [
  'nivelEstudioNoCulminado', 'tipoTituloNoCulminado', 'tituloNoCulminado', 'institucionNoCulminada',
  'localidadNoCulminada', 'promedioNoCulminado', 'anioInicioNoCulminado', 'anioFinNoCulminado',
];

// El campo que se está validando no cuenta como "uso de la sección" (si no, se auto-exigiría).
const seccionUsada = (values, campos, propio) =>
  campos.some((campo) => campo !== propio && !!values[campo]);

// Reglas del paso, en un solo lugar. Ejes de variación: oyente (no valida nada), showMidStudies
// y esPosgrado (posgrado exige estudios superiores, el resto los toma como opcionales).
const getValidations = (isListener, showMidStudies, esPosgrado) => {
  if (isListener || !showMidStudies) return {};

  const obligatorio = (value) => (!value ? "El campo es obligatorio" : null);
  const soloLetras = (value) => (/^[A-Za-zÁ-Üá-üÑñ\s]{3,60}$/.test(value || '') ? null : "Sólo letras (mín. 3)");
  const soloLetrasSiCompleta = (value) => (!value ? null : soloLetras(value));

  // Superior: obligatoria en posgrado; en el resto solo se exige si el usuario ya empezó a cargarla.
  const exigeSuperior = (values, propio) => esPosgrado || seccionUsada(values, CAMPOS_SUPERIOR, propio);

  // No culminados: "No especificado" / "No hizo estudios" declaran ausencia de estudios, así que
  // nunca activan la sección (antes elegir cualquiera de las dos volvía obligatorio medio formulario).
  const exigeNoCulminado = (values, propio) =>
    !SIN_ESTUDIOS_NO_CULMINADO.includes(values.nivelEstudioNoCulminado) &&
    seccionUsada(values, CAMPOS_NO_CULMINADO, propio);

  return {
    // Educación Secundaria (siempre obligatoria)
    tituloSecundario: soloLetras,
    institucionSecundaria: soloLetras,
    paisSecundario: obligatorio,
    // Igual que Superior y No Culminados: con país != AR la UI muestra un TextInput atado a
    // provinciaDescripcionSecundario y el Select de provincias ni se renderiza.
    provinciaSecundario: (value, values) =>
      values.paisSecundario !== 'AR' ? null : obligatorio(value),
    provinciaDescripcionSecundario: (value, values) =>
      values.paisSecundario === 'AR' ? null : (value ? null : "Ingrese la provincia"),
    localidadSecundaria: soloLetras,
    anioEgresoSecundario: obligatorio,

    // Educación Superior
    tipoTituloSuperior: (value) => (esPosgrado ? obligatorio(value) : null),
    tituloSuperior: (value) => (esPosgrado ? soloLetras(value) : soloLetrasSiCompleta(value)),
    institucionSuperior: (value) => (esPosgrado ? soloLetras(value) : soloLetrasSiCompleta(value)),
    localidadSuperior: (value) => (esPosgrado ? soloLetras(value) : soloLetrasSiCompleta(value)),
    promedioSuperior: (value) =>
      esPosgrado && !value ? "Ingrese un promedio válido (0-10)" : null,
    anioEgresoSuperior: (value, values) =>
      exigeSuperior(values, 'anioEgresoSuperior') ? obligatorio(value) : null,
    paisSuperior: (value, values) =>
      exigeSuperior(values, 'paisSuperior') ? obligatorio(value) : null,
    provinciaSuperior: (value, values) => {
      if (!exigeSuperior(values, 'provinciaSuperior')) return null;
      if (values.paisSuperior !== 'AR') return null;
      return value ? null : "Seleccione una provincia";
    },
    provinciaDescripcionSuperior: (value, values) => {
      if (!exigeSuperior(values, 'provinciaDescripcionSuperior')) return null;
      if (values.paisSuperior === 'AR') return null;
      return value ? null : "Ingrese la provincia";
    },

    // Carreras no culminadas (siempre opcionales; se exigen solo si se empezó a cargar la sección)
    tituloNoCulminado: soloLetrasSiCompleta,
    institucionNoCulminada: soloLetrasSiCompleta,
    localidadNoCulminada: soloLetrasSiCompleta,
    anioInicioNoCulminado: (value, values) =>
      exigeNoCulminado(values, 'anioInicioNoCulminado') ? obligatorio(value) : null,
    anioFinNoCulminado: (value, values) => {
      if (!exigeNoCulminado(values, 'anioFinNoCulminado')) return null;
      if (!value) return "El campo es obligatorio";
      if (values.anioInicioNoCulminado && parseInt(value, 10) < parseInt(values.anioInicioNoCulminado, 10)) {
        return "El año de fin debe ser posterior al año de inicio";
      }
      return null;
    },
    paisNoCulminado: (value, values) =>
      exigeNoCulminado(values, 'paisNoCulminado') ? obligatorio(value) : null,
    provinciaNoCulminado: (value, values) => {
      if (!exigeNoCulminado(values, 'provinciaNoCulminado')) return null;
      if (values.paisNoCulminado !== 'AR') return null;
      return value ? null : "Seleccione una provincia";
    },
    provinciaDescripcionNoCulminado: (value, values) => {
      if (!exigeNoCulminado(values, 'provinciaDescripcionNoCulminado')) return null;
      if (values.paisNoCulminado === 'AR') return null;
      return value ? null : "Ingrese la provincia";
    },
  };
};

const StudiesDataForm = forwardRef(({ stepNumber, queryParams, id }, ref) => {
  
  const t = queryParams?.t;

  const dispatch = useDispatch();

  const response = useSelector( (state) => state.respuestas.steps[stepNumber]?.responses || {} );
  const { data: dataPaises } = useSelector((state) => state.datosFormulario.paises);
  const { data: dataProvincias } = useSelector((state) => state.datosFormulario.provincias);
  const { data: cueData, loading: loadingCue } = useSelector((state) => state.datosFormulario.cue);
  const homologationResponse = useStepResponses(stepIds.HOMOLOGACION);
  
  const isListener = homologationResponse.catAlumno === 'oyente';
  const showMidStudies = !(homologationResponse.declaracion && t === 'g');
  const esPosgrado = t === 'p';
  const { track } = useFormTracking(stepNumber);
  const trackSelect = (fieldName) => (value) => {
    form.setFieldValue(fieldName, value);
    if (value) track('field_completed', { fieldName });
  };

  const trackOnBlur = (fieldName) => () => {
    if (form.values[fieldName]) track('field_completed', { fieldName });
  };
  const [exampleModalOpen, setExampleModalOpen] = useState(false)


  const form = useForm({
    initialValues: {
      //Educación Secundaria
      tituloSecundario: response.tituloSecundario || "",
      cueSecundario: response.cueSecundario || "",
      institucionSecundaria: response.institucionSecundaria || "",
      paisSecundario: response.paisSecundario || 'AR',
      provinciaSecundario: response.provinciaSecundario || null,
      provinciaDescripcionSecundario: response.provinciaDescripcionSecundario || null,
      localidadSecundaria: response.localidadSecundaria || "",
      anioEgresoSecundario: response.anioEgresoSecundario || null,
      promedioSecundario: response.promedioSecundario || "",
      direccionSecundaria:response.direccionSecundaria || "",
      // true = institución/provincia/localidad/dirección vienen del padrón por el CUE. Se persiste con el
      // resto del paso para que, al volver al paso o recargar, se sepa qué se autocompletó.
      cueAutocompletado: response.cueAutocompletado || false,

      //Educacion Superior
      tipoTituloSuperior: response.tipoTituloSuperior || null,
      tituloSuperior: response.tituloSuperior || "",
      institucionSuperior: response.institucionSuperior || "",
      paisSuperior: response.paisSuperior || 'AR',
      provinciaSuperior: response.provinciaSuperior || null,
      localidadSuperior: response.localidadSuperior || "",
      provinciaDescripcionSuperior: response.provinciaDescripcionSuperior || null,
      anioEgresoSuperior: response.anioEgresoSuperior || null,
      promedioSuperior: response.promedioSuperior || "",

      //Educacion Universitaria/Cursos no Culminados
      nivelEstudioNoCulminado: response.nivelEstudioNoCulminado || null,
      tipoTituloNoCulminado: response.tipoTituloNoCulminado || null,
      tituloNoCulminado: response.tituloNoCulminado || "",
      institucionNoCulminada: response.institucionNoCulminada || "",
      anioInicioNoCulminado: response.anioInicioNoCulminado || null,
      anioFinNoCulminado: response.anioFinNoCulminado || null,
      promedioNoCulminado: response.promedioNoCulminado || "",
      paisNoCulminado: response.paisNoCulminado || 'AR',
      provinciaNoCulminado: response.provinciaNoCulminado || null,
      provinciaDescripcionNoCulminado: response.provinciaDescripcionNoCulminado || null,
      localidadNoCulminada: response.localidadNoCulminada || "",
    },
    validate: getValidations(isListener, showMidStudies, esPosgrado),
  });
  
  const [debouncedValues] = useDebouncedValue(form.values, 300);
    
  useEffect(() => {
    dispatch(SaveResponses(id, debouncedValues));
  }, [debouncedValues, dispatch, id]);

  useEffect(() => {
    dispatch(GetPaises())
  }, [])

  // --- CUE -------------------------------------------------------------------------------------------
  // Con un CUE de 9 dígitos se busca la escuela en el padrón y se completan institución, provincia,
  // localidad y dirección. Antes: (1) acortar o borrar el CUE vaciaba el país (que seguía mostrando
  // "Argentina") y lo escrito a mano; (2) la provincia se guardaba en "provinciaSecundaria", un campo que
  // no existe en el form (el Select, la validación y el payload usan provinciaSecundario), así que
  // quedaba vacía y bloqueada; (3) el input del CUE no estaba atado al form y al volver al paso se veía
  // vacío con los campos bloqueados.
  const cueBuscado = useRef(null); // CUE de la búsqueda en curso; null = los datos del store son viejos

  const limpiarAutocompletadoCue = () => {
    // Solo lo que completó el padrón y solo si se completó: nunca el país ni lo escrito a mano.
    form.setValues((prev) => (prev.cueAutocompletado ? {
      ...prev,
      cueAutocompletado: false,
      institucionSecundaria: "",
      provinciaSecundario: null,
      provinciaDescripcionSecundario: null,
      localidadSecundaria: "",
      direccionSecundaria: "",
    } : prev));
  };

  useEffect(() => {
    // Datos del store que no vienen de una búsqueda de esta sesión del paso (ej. persistidos de antes).
    if (!cueBuscado.current || loadingCue) return;
    const cue = cueBuscado.current;
    cueBuscado.current = null;
    if (cue !== form.values.cueSecundario) return; // la persona siguió editando el CUE

    const escuela = cueData?.[0];
    if (!escuela) {
      limpiarAutocompletadoCue();
      return;
    }
    // El padrón trae el código de provincia (mismo value que el Select); el nombre queda de respaldo.
    const provincia = dataProvincias?.find((p) => p.value === escuela.provincia)
      || dataProvincias?.find((p) => p.label.toLowerCase().trim() === (escuela.provinciaDescripcion || '').toLowerCase().trim());
    form.setValues((prev) => ({
      ...prev,
      cueAutocompletado: true,
      paisSecundario: 'AR',
      institucionSecundaria: escuela.nombre || "",
      provinciaSecundario: provincia?.value || null,
      provinciaDescripcionSecundario: provincia?.label || null,
      localidadSecundaria: escuela.localidad || "",
      direccionSecundaria: escuela.domicilio || "",
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- solo reacciona al resultado de la búsqueda
  }, [cueData, loadingCue]);

  // Un campo queda bloqueado solo si lo completó el padrón: si el padrón no trajo algo (ej. una provincia
  // que no está en la lista), la persona lo puede completar.
  const bloqueadoPorCue = (campo) => !!form.values.cueAutocompletado && !!form.values[campo];

  const onCueEscrito = useRef(null);
  onCueEscrito.current = (value) => {
    if (value && value.length === 9) {
      track('field_completed', { fieldName: 'cueSecundario' });
      cueBuscado.current = value;
      dispatch(GetCue(value));
    } else {
      cueBuscado.current = null;
      limpiarAutocompletadoCue();
    }
  };
  // Estable entre renders: con el input controlado, un debounce nuevo por render dispararía una búsqueda
  // por cada tecla.
  const buscarCue = useMemo(() => debounce((value) => onCueEscrito.current(value), 700), []);

  const handleCueChange = (event) => {
    const value = event.target.value;
    form.setFieldValue('cueSecundario', value);
    buscarCue(value);
  };

  useImperativeHandle(ref, () => ({
    validate: () => {
      const validation = form.validate();
      if (validation.hasErrors) {
        Object.keys(validation.errors).forEach((field) =>
          track('field_error', { fieldName: field })
        );
        return false;
      }
      return true;
    },
  }));

  const sinEstudiosNoCulminado = SIN_ESTUDIOS_NO_CULMINADO.includes(form.values.nivelEstudioNoCulminado);

  const handleSelectNivelNoCulminado = (value) => {
    if (SIN_ESTUDIOS_NO_CULMINADO.includes(value)) {
      form.setValues({
        nivelEstudioNoCulminado: value,
        tipoTituloNoCulminado: null,
        tituloNoCulminado: "",
        institucionNoCulminada: "",
        anioInicioNoCulminado: null,
        anioFinNoCulminado: null,
        promedioNoCulminado: "",
        paisNoCulminado: 'AR',
        provinciaNoCulminado: null,
        provinciaDescripcionNoCulminado: null,
        localidadNoCulminada: "",
      });
      // Solo los errores de esta sección: clearErrors() borraría también los del secundario.
      CAMPOS_NO_CULMINADO.concat('paisNoCulminado', 'provinciaNoCulminado', 'provinciaDescripcionNoCulminado')
        .forEach((campo) => form.clearFieldError(campo));
    } else {
      form.setFieldValue('nivelEstudioNoCulminado', value);
    }
    if (value) track('field_completed', { fieldName: 'nivelEstudioNoCulminado' });
  };

  const handleSavePais = (campo, pais) => {
    form.setValues({[`pais${campo}`]: pais});
    form.setValues({[`provincia${campo}`]: null});
    form.setValues({[`provinciaDescripcion${campo}`]: null});
    if (pais) track('field_completed', { fieldName: `pais${campo}` });
  };

  const handleSaveResponseProvincia = (campo, provincia) => {
    let prov = dataProvincias.find((p) => p.value === provincia);
    if (prov) {
      form.setValues({[`provincia${campo}`]: prov.value});
      form.setValues({[`provinciaDescripcion${campo}`]: prov.label});
    } else {
      form.setValues({[`provincia${campo}`]: 'OTR'});
      form.setValues({[`provinciaDescripcion${campo}`]: provincia});
    }
    if (provincia) track('field_completed', { fieldName: `provincia${campo}` });
  };

  return (
    <Container size="100%" >
      {isListener ? (
        <Group justify="center">
          <Alert maw={'60%'} m={'2rem'} variant="transparent" color="#f20a61" radius="md" title="Este paso no es requerido" icon={<IconInfoCircle/>}>
            Dado que seleccionaste la opción <b>"Oyente"</b>, no es necesario que completes este formulario. Puedes proceder directamente al siguiente paso del proceso.
          </Alert>
        </Group>
      ):(
        <>
          {showMidStudies && (
            <>
              <Text size="xl" fw={500} mb={'xs'} c={'rgb(85, 85, 85)'}>Estudios de nivel medio - (Secundario)</Text>
              <Row className='row-inputs'>
                <Col md={4}>
                  <TextInput
                    required
                    label="Título"
                    placeholder='Bachiller en Ciencias Sociales'
                    {...form.getInputProps("tituloSecundario")}
                    onKeyDown={(e)=>{ if(!/[A-Za-zÁ-Üá-üÑñ\s]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Tab') e.preventDefault(); }}
                    onBlur={trackOnBlur('tituloSecundario')}
                  />
                </Col>
                <Col md={4}>
                <TextInput
                  label={
                    <Group spacing={3}> {/* Reducimos el espacio entre el texto y el enlace */}
                      <Text 
                      style={{ 
                        fontFamily: 'Montserrat, serif', // Aplica la fuente Greycliff CF
                        fontSize: '14px', // Tamaño de la fuente
                        fontWeight:'500',
                      }}
                    >
                      CUE
                    </Text>
                      <Anchor
                        onClick={() => setExampleModalOpen(true)}
                        style={{
                          color: 'black', // Color inicial negro
                          fontSize: '14px',
                          fontFamily: 'Montserrat, serif',
                          fontWeight:'500',
                          cursor: 'pointer',
                          textDecoration:'underline',
                          marginLeft: '1px', // Menos espacio entre el texto y el enlace
                        }}
                        onMouseEnter={(e) => (e.target.style.color = '#f20a61')} // Cambia el color al pasar el mouse
                        onMouseLeave={(e) => (e.target.style.color = 'black')} // Vuelve al color inicial al 
                      >
                        (ver ejemplo)
                      </Anchor>
                    </Group>
                  }
                  placeholder="Ingrese el CUE"
                  type="number"
                  value={form.values.cueSecundario}
                  onChange={handleCueChange}
                  rightSection={
                    <Group spacing={5}>
                      {loadingCue && <Loader color="pink" size="sm" />}
                      {form.values.direccionSecundaria && (
                        <Tooltip
                          label={form.values.direccionSecundaria}
                          position="top"
                          withArrow
                          transitionProps={{ transition: "fade", duration: 200 }}
                        >
                          <IconMapPin size="1.2rem" style={{ color: "#f20a61", cursor: "pointer" }} />
                        </Tooltip>
                      )}
                    </Group>
                  }
                  rightSectionWidth={50}
                />
                </Col>
                <Col md={4}>
                  <TextInput
                    required
                    label="Institución"
                    placeholder='Colegio Esteban Echeverría'
                    {...form.getInputProps("institucionSecundaria")}
                    disabled={bloqueadoPorCue('institucionSecundaria')}
                    onKeyDown={(e)=>{ if(!/[A-Za-zÁ-Üá-üÑñ\s]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Tab') e.preventDefault(); }}
                    onBlur={trackOnBlur('institucionSecundaria')}
                  />
                </Col>
              </Row>
              <Row className='row-inputs'>
                <Col md={4}>
                  <Select
                    required
                    searchable
                    label="País"
                    data={dataPaises}
                    checkIconPosition='right'
                    placeholder='Argentina'
                    {...form.getInputProps("paisSecundario", {
                      onChange: (value) => {
                        handleSavePais('Secundario',value);
                      },
                    })}
                    disabled={bloqueadoPorCue('paisSecundario')}
                  />
                </Col>
                <Col md={4}>
                  {form.values.paisSecundario==='AR' ?(
                    <Select
                      required
                      searchable
                      label="Provincia"
                      data={dataProvincias}
                      checkIconPosition='right'
                      placeholder='Seleccione una opción'
                      {...form.getInputProps("provinciaSecundario", {
                        onChange: (value) => {
                          handleSaveResponseProvincia('Secundario',value);
                        },
                      })}
                      disabled={bloqueadoPorCue('provinciaSecundario')}
                    />
                  ):( 
                    <TextInput
                      required
                      label="Provincia"
                      placeholder="Ingrese la provincia"
                      {...form.getInputProps("provinciaDescripcionSecundario", {
                        onChange: (e) => {
                          handleSaveResponseProvincia('Secundario',e.target.value);
                        },
                      })}
                    />
                  )}
                </Col>
                <Col md={4}>
                  <TextInput
                    required
                    label="Localidad"
                    placeholder='CABA'
                    {...form.getInputProps("localidadSecundaria")}
                    disabled={bloqueadoPorCue('localidadSecundaria')}
                    onKeyDown={(e)=>{ if(!/[A-Za-zÁ-Üá-üÑñ\s]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Tab') e.preventDefault(); }}
                    onBlur={trackOnBlur('localidadSecundaria')}
                  />
                </Col>
              </Row>
              <Row className='row-inputs'>
                <Col md={6}>
                  <Select
                    required
                    label="Año que egresó"
                    checkIconPosition='right'
                    data={years.map(year => ({
                        value: year.toString(),
                        label: year.toString(),
                    }))}
                    placeholder="Seleccionar año"
                    //onChange={(value) => handleSaveResponse('anioEgresoSecundario',value)}
                    {...form.getInputProps("anioEgresoSecundario")}
                    onChange={trackSelect('anioEgresoSecundario')}
                  />
                </Col>
                <Col md={6}>
                  <NumberInput
                    label="Promedio"
                    min={0}
                    max={10}
                    placeholder='7.00'
                    {...form.getInputProps("promedioSecundario")}
                    onBlur={trackOnBlur('promedioSecundario')}
                  />
                </Col>
              </Row>
            </>
          )}
          <Text size="xl" fw={500} mb={'xs'} mt={'xl'} c={'rgb(85, 85, 85)'}>Estudios de nivel superior</Text>
          <Row className='row-inputs'>
            <Col md={4}>
              <Select
                required={esPosgrado}
                label="Tipo Título"
                data={TypeTitleEnum}
                placeholder='Seleccione una opción'
                //onChange={(value) => handleSaveResponse('tipoTituloSuperior',value)}
                {...form.getInputProps("tipoTituloSuperior")}
                onChange={trackSelect('tipoTituloSuperior')}
              />
            </Col>
            <Col md={4}>
              <TextInput
                required={esPosgrado}
                label="Título"
                placeholder='Técnico/a en Refrigeración'
                {...form.getInputProps("tituloSuperior")}
                onKeyDown={(e)=>{ if(!/[A-Za-zÁ-Üá-üÑñ\s]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Tab') e.preventDefault(); }}
                onBlur={trackOnBlur('tituloSuperior')}
              />
            </Col>
            <Col md={4}>
              <TextInput
                required={esPosgrado}
                label="Institución"
                placeholder='UAI'
                {...form.getInputProps("institucionSuperior")}
                onKeyDown={(e)=>{ if(!/[A-Za-zÁ-Üá-üÑñ\s]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Tab') e.preventDefault(); }}
                onBlur={trackOnBlur('institucionSuperior')}
              />
            </Col>
          </Row>
          <Row className='row-inputs'>
            <Col md={4}>
              <Select
                required={esPosgrado}
                searchable
                label="País"
                data={dataPaises}
                checkIconPosition='right'
                placeholder='Seleccione un país'
                {...form.getInputProps("paisSuperior", {
                  onChange: (value) => {
                    handleSavePais('Superior',value);
                  },
                })}
              />
            </Col>
            <Col md={4}>
              {form.values.paisSuperior ==='AR' ? (
                <Select
                  required={esPosgrado}
                  searchable
                  label="Provincia"
                  data={dataProvincias}
                  checkIconPosition='right'
                  placeholder='Seleccione una provincia'
                  {...form.getInputProps("provinciaSuperior", {
                    onChange: (value) => {
                      handleSaveResponseProvincia('Superior',value);
                    },
                  })}
                />
              ) : (
                <TextInput
                  required={esPosgrado}
                  label="Provincia"
                  placeholder="Ingrese la provincia"
                  {...form.getInputProps("provinciaDescripcionSuperior", {
                    onChange: (e) => {
                      handleSaveResponseProvincia('Superior',e.target.value);
                    },
                  })}
                />
              )}
            </Col>
            <Col md={4}>
              <TextInput
                required={esPosgrado}
                label="Localidad"
                placeholder='Ingrese una localidad'
                {...form.getInputProps("localidadSuperior")}
                onKeyDown={(e)=>{ if(!/[A-Za-zÁ-Üá-üÑñ\s]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Tab') e.preventDefault(); }}
                onBlur={trackOnBlur('localidadSuperior')}
              />
            </Col>
          </Row>
          <Row className='row-inputs'>
            <Col md={6}>
              <Select
                required={esPosgrado}
                label="Año que egresó"    
                checkIconPosition='right'       
                data={years.map(year => ({
                    value: year.toString(),
                    label: year.toString(),
                }))}
                placeholder="Seleccionar año"
                //onChange={(value) => handleSaveResponse('anioEgresoSuperior',value)}
                {...form.getInputProps("anioEgresoSuperior")}
                onChange={trackSelect('anioEgresoSuperior')}
              />
            </Col>
            <Col md={6}>
              <NumberInput
                required={esPosgrado}
                label="Promedio"
                min={0}
                max={10}
                placeholder='7.00'
                {...form.getInputProps("promedioSuperior")}
                onBlur={trackOnBlur('promedioSuperior')}
              />
            </Col>
          </Row>
    
          <Text size="xl" fw={500} mb={'xs'} mt={'xl'} c={'rgb(85, 85, 85)'}>Carreras Terciarias ó Universitarias cursadas y no culminadas</Text>
          <Row>
            <Col md={4}>
              <Select
                label="Nivel máximo de estudios no culminados:"
                data={StudyLevelEnum}
                checkIconPosition='right'
                placeholder='Seleccione una opción'
                //onChange={(value) => handleSaveResponse('nivelEstudioNoCulminado',value)}
                {...form.getInputProps("nivelEstudioNoCulminado")}
                onChange={handleSelectNivelNoCulminado}
              />
            </Col>
            <Col md={4}>
              <Select           
                label="Tipo Título"
                data={TypeTitleEnum}
                checkIconPosition='right'
                placeholder='Seleccione el tipo de título'
                //onChange={(value) => handleSaveResponse('tipoTituloNoCulminado',value)}
                disabled={sinEstudiosNoCulminado}
                {...form.getInputProps("tipoTituloNoCulminado")}
                onChange={trackSelect('tipoTituloNoCulminado')}
              />
            </Col>
            <Col md={4}>
              <TextInput
                label="Título"
                placeholder='Ingrese el nombre del título'
                disabled={sinEstudiosNoCulminado}
                {...form.getInputProps("tituloNoCulminado")}
                onKeyDown={(e)=>{ if(!/[A-Za-zÁ-Üá-üÑñ\s]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Tab') e.preventDefault(); }}
                onBlur={trackOnBlur('tituloNoCulminado')}
              />
            </Col>
          </Row>
          <Row>
            <Col md={3}>
              <TextInput
                label="Institución"
                placeholder='Universidad Abierta Interamericana'
                disabled={sinEstudiosNoCulminado}
                {...form.getInputProps("institucionNoCulminada")}
                onKeyDown={(e)=>{ if(!/[A-Za-zÁ-Üá-üÑñ\s]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Tab') e.preventDefault(); }}
                onBlur={trackOnBlur('institucionNoCulminada')}
              />
            </Col>
            <Col md={3}>
              <Select
                label="Año de inicio de cursada"    
                checkIconPosition='right'       
                data={years.map(year => ({
                    value: year.toString(),
                    label: year.toString(),
                }))}
                placeholder="Seleccionar año"
                //onChange={(value) => handleSaveResponse('anioInicioNoCulminado',value)}
                disabled={sinEstudiosNoCulminado}
                {...form.getInputProps("anioInicioNoCulminado")}
                onChange={trackSelect('anioInicioNoCulminado')}
              />
            </Col>
            <Col md={3}>
              <Select
                label="Ultimo año de cursada"  
                checkIconPosition='right'         
                data={years.map(year => ({
                    value: year.toString(),
                    label: year.toString(),
                }))}
                placeholder="Seleccionar año"
                //onChange={(value) => handleSaveResponse('anioFinNoCulminado',value)}
                disabled={sinEstudiosNoCulminado}
                {...form.getInputProps("anioFinNoCulminado")}
                onChange={trackSelect('anioFinNoCulminado')}
              />
            </Col>
            <Col md={3}>
              <NumberInput
                label="Promedio"
                placeholder='7.00'
                min={0}
                max={10}
                disabled={sinEstudiosNoCulminado}
                {...form.getInputProps("promedioNoCulminado")}
                onBlur={trackOnBlur('promedioNoCulminado')}
              />
            </Col>
          </Row>
          <Row>
            <Col md={4}>
              <Select
                searchable
                label="País"
                data={dataPaises}
                checkIconPosition='right'
                placeholder='Seleccione una opción'
                disabled={sinEstudiosNoCulminado}
                {...form.getInputProps("paisNoCulminado", {
                  onChange: (value) => {
                    handleSavePais('NoCulminado',value);
                  },
                })}
              />
            </Col>
            <Col md={4}>
              {form.values.paisNoCulminado ==='AR' ? (
                <Select
                  searchable
                  label="Provincia"
                  data={dataProvincias}
                  checkIconPosition='right'
                  placeholder='Seleccione una provincia'
                  disabled={sinEstudiosNoCulminado}
                  {...form.getInputProps("provinciaNoCulminado", {
                    onChange: (value) => {
                      handleSaveResponseProvincia('NoCulminado',value);
                    },
                  })}
                />
              ):(
                <TextInput
                  label="Provincia"
                  placeholder="Ingrese la provincia"
                  disabled={sinEstudiosNoCulminado}
                  {...form.getInputProps("provinciaDescripcionNoCulminado", {
                    onChange: (e) => {
                      handleSaveResponseProvincia('NoCulminado',e.target.value);
                    },
                  })}
                />
              )}
            </Col>
            <Col md={4}>
              <TextInput
                label="Localidad"
                placeholder='Ingrese una localidad'
                disabled={sinEstudiosNoCulminado}
                {...form.getInputProps("localidadNoCulminada")}
                onKeyDown={(e)=>{ if(!/[A-Za-zÁ-Üá-üÑñ\s]/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Tab') e.preventDefault(); }}
                onBlur={trackOnBlur('localidadNoCulminada')}
              />
            </Col>
          </Row>
          <Modal
            opened={exampleModalOpen}
            onClose={() => setExampleModalOpen(false)}
            title="Clave Única de identificación que poseen todos los establecimientos educativos reconocidos en forma oficial"
            size="xl" // Cambia el tamaño del modal a "xl" para que sea más grande
          >
            <Image
              src={imagenCue}
              alt="Ejemplo de CUE"
              fit="contain"
              style={{
                width: '100%', // Asegura que ocupe todo el ancho disponible
                height: 'auto', // Mantiene la proporción original de la imagen
                maxHeight: '600px', // Aumenta la altura máxima para que la imagen sea más grande
              }}
            />
          </Modal>
        </>
      )}
    </Container>
  );
});

export default StudiesDataForm;
